import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ShoppingCart, Palette, ChevronLeft, Check, Box, ChevronDown } from 'lucide-react';
import api from '../services/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useCart } from '../context/CartContext';
import ProductPreview3D from '../components/ProductPreview3D';
import { sanitizeHTML } from '../utils/sanitize';

const RICH_TEXT_CLASSES = 'rich-text-content text-slate-600 leading-relaxed [&_h1]:text-2xl [&_h1]:font-bold [&_h1]:mt-3 [&_h1]:mb-2 [&_h2]:text-xl [&_h2]:font-semibold [&_h2]:mt-3 [&_h2]:mb-2 [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:mt-2 [&_h3]:mb-1 [&_p]:mb-2 [&_a]:text-[#2a9d8f] [&_a]:underline [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-2 [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:mb-2';

const ProductDetail = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedColor, setSelectedColor] = useState('');
  const [selectedSize, setSelectedSize] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [adding, setAdding] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [show3D, setShow3D] = useState(false);
  const [useColorImage, setUseColorImage] = useState(false);
  const [useSizeImage, setUseSizeImage] = useState(true);
  const [selectedPrintSize, setSelectedPrintSize] = useState('');
  const [selectedPrintQuality, setSelectedPrintQuality] = useState('');
  const has3D = product && !['nametag', 'calendar'].includes(product.model_type) && !['namnskylt', 'kalender', 'fotoalbum'].includes(product.category);

  // Show admin-managed Size = Price selector when sizes are configured
  const hasAdminSizePricing = product?.available_sizes?.length > 0 && product?.size_quality_prices?.length > 0;
  const hasQualities = product?.available_qualities?.length > 0;

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const response = await api.get(`/products/${productId}`);
        setProduct(response.data);
        if (response.data.colors?.length > 0) {
          setSelectedColor(response.data.colors[0]);
        }
        if (response.data.sizes?.length > 0) {
          setSelectedSize(response.data.sizes[0]);
        }
        import('../utils/analytics').then(({ trackViewItem }) => trackViewItem(response.data));
        import('../utils/metaPixel').then(({ trackMetaViewContent }) => trackMetaViewContent(response.data));
      } catch {
        toast.error('Kunde inte ladda produkten');
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [productId]);

  // Set default print size/quality from product
  useEffect(() => {
    if (hasAdminSizePricing && product) {
      if (product.available_sizes?.length) setSelectedPrintSize(product.available_sizes[0]);
      if (product.available_qualities?.length) setSelectedPrintQuality(product.available_qualities[0]);
      else setSelectedPrintQuality('Standard');
    }
  }, [hasAdminSizePricing, product]);

  const resolveImageUrl = (url) => {
    if (!url) return '';
    return url.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url;
  };

  // Get the image for the currently selected color (if available)
  const getActiveImage = () => {
    // Priority: color image > size image (unless overridden by manual thumbnail click) > selected thumbnail > first product image
    if (useColorImage && selectedColor && product?.color_images?.[selectedColor]) {
      return resolveImageUrl(product.color_images[selectedColor]);
    }
    const activeSizeKey = hasAdminSizePricing ? selectedPrintSize : selectedSize;
    if (useSizeImage && activeSizeKey && product?.size_images?.[activeSizeKey]) {
      return resolveImageUrl(product.size_images[activeSizeKey]);
    }
    return resolveImageUrl(product?.images?.[selectedImageIndex] || product?.images?.[0]);
  };

  const handleAddToCart = async () => {
    setAdding(true);
    try {
      const cartItem = {
        product_id: product.product_id,
        name: product.name,
        price: product.price,
        image: getActiveImage(),
        quantity,
        color: selectedColor || null,
        size: selectedSize || null
      };
      // Add photo print options if applicable
      if (hasAdminSizePricing && selectedPrintSize) {
        const quality = selectedPrintQuality || 'Standard';
        const priceEntry = product.size_quality_prices?.find(p => p.size === selectedPrintSize && (p.quality || 'Standard') === quality);
        cartItem.print_size = selectedPrintSize;
        cartItem.print_quality = quality;
        if (priceEntry) cartItem.price = priceEntry.price;
      }
      await addToCart(cartItem);
      toast.success('Tillagd i varukorgen!', {
        action: {
          label: 'Visa varukorg',
          onClick: () => navigate('/varukorg')
        }
      });
    } catch {
      toast.error('Kunde inte lägga till i varukorgen');
    } finally {
      setAdding(false);
    }
  };

  const handleDesign = () => {
    const sizeForEditor = hasAdminSizePricing ? selectedPrintSize : selectedSize;
    let resolvedPrice = product?.price;
    if (hasAdminSizePricing && selectedPrintSize) {
      const quality = selectedPrintQuality || 'Standard';
      const entry = product.size_quality_prices?.find(p => p.size === selectedPrintSize && (p.quality || 'Standard') === quality);
      if (entry) resolvedPrice = entry.price;
    }
    const sharedState = {
      size: sizeForEditor,
      print_size: selectedPrintSize || null,
      print_quality: selectedPrintQuality || null,
      resolved_price: resolvedPrice,
    };
    if (product?.model_type === 'calendar' || product?.category === 'kalender') {
      navigate(`/kalender/${productId}`, { state: sharedState });
    } else if (product?.model_type === 'nametag' || product?.category === 'namnskylt') {
      navigate(`/namnskylt/${productId}`, { state: sharedState });
    } else if (product?.category === 'fotoalbum') {
      navigate(`/fotoalbum/${productId}`, { state: sharedState });
    } else {
      navigate(`/design/${productId}`, { state: { color: selectedColor, ...sharedState } });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-slate-500 mb-4">Produkten hittades inte</p>
          <Link to="/produkter">
            <Button variant="outline">Tillbaka till produkter</Button>
          </Link>
        </div>
      </div>
    );
  }

  const colorHexMap = {
    'Vit': '#FFFFFF',
    'Svart': '#1a1a1a',
    'Grå': '#6b7280',
    'Beige': '#d4c4a8',
    'Marinblå': '#1e3a5f',
    'Naturvit': '#f5f5dc',
    'Transparent': '#e5e5e5',
    'Röd': '#dc2626',
    'Blå': '#2563eb',
    'Grön': '#16a34a',
    'Rosa': '#ec4899',
    'Gul': '#eab308',
    'Orange': '#ea580c',
    'Lila': '#7c3aed',
    'Brun': '#78350f',
    'Ljusgrå': '#d1d5db',
    'Mörkgrå': '#374151',
    'Khaki': '#a3956b',
    'Olivgrön': '#4d5c2e',
    'Bordeaux': '#722f37',
    'Teal': '#2a9d8f',
    'Vit bakgrund': '#FFFFFF',
    'Färgglad bakgrund': '#f472b6',
  };

  return (
    <div className="min-h-screen" data-testid="product-detail-page">
      {/* Breadcrumb */}
      <div className="bg-slate-50 py-4">
        <div className="container-main">
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <Link to="/" className="hover:text-primary">Hem</Link>
            <span>/</span>
            <Link to="/produkter" className="hover:text-primary">Produkter</Link>
            <span>/</span>
            <span className="text-slate-700">{product.name}</span>
          </div>
        </div>
      </div>

      <div className="container-main py-12">
        <div className="grid lg:grid-cols-2 gap-12">
          {/* Left - Preview */}
          <div className="lg:sticky lg:top-24 lg:h-fit space-y-3">
            {/* Main image display */}
            <div className="relative">
              <div className="aspect-square rounded-xl overflow-hidden bg-slate-50 flex items-center justify-center">
                {show3D && has3D ? (
                  <ProductPreview3D 
                    modelType={product.model_type}
                    color={colorHexMap[selectedColor] || '#FFFFFF'}
                    productImage={getActiveImage()}
                  />
                ) : (
                  <img
                    src={getActiveImage()}
                    alt={product.name}
                    className="w-full h-full object-contain"
                    data-testid="product-main-image"
                  />
                )}
              </div>
              {/* Navigation arrows */}
              {!show3D && product.images?.length > 1 && (
                <>
                  <button
                    onClick={() => { setSelectedImageIndex(prev => prev > 0 ? prev - 1 : product.images.length - 1); setUseColorImage(false); setUseSizeImage(false); }}
                    className="absolute left-2 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-white/90 hover:bg-white shadow-lg flex items-center justify-center transition-transform hover:scale-105"
                    data-testid="prev-image"
                  >
                    <ChevronLeft className="w-5 h-5 text-slate-700" />
                  </button>
                  <button
                    onClick={() => { setSelectedImageIndex(prev => prev < product.images.length - 1 ? prev + 1 : 0); setUseColorImage(false); setUseSizeImage(false); }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-white/90 hover:bg-white shadow-lg flex items-center justify-center rotate-180 transition-transform hover:scale-105"
                    data-testid="next-image"
                  >
                    <ChevronLeft className="w-5 h-5 text-slate-700" />
                  </button>
                </>
              )}
              {/* 3D toggle button */}
              {has3D && (
                <button
                  onClick={() => setShow3D(prev => !prev)}
                  className={`absolute top-3 right-3 z-20 px-3 py-1.5 rounded-full text-xs font-medium shadow-lg flex items-center gap-1.5 transition-colors ${
                    show3D ? 'bg-[#2a9d8f] text-white' : 'bg-white/90 hover:bg-white text-slate-700'
                  }`}
                  data-testid="toggle-3d"
                >
                  <Box className="w-3.5 h-3.5" />
                  {show3D ? '3D aktiv' : '3D vy'}
                </button>
              )}
            </div>
            {show3D && has3D && (
              <p className="text-center text-sm text-slate-400">
                Dra för att rotera • Scrolla för att zooma
              </p>
            )}
            {/* Thumbnails row */}
            {!show3D && product.images?.length > 1 && (
              <div className="flex gap-2 overflow-x-auto pb-1" data-testid="image-thumbnails">
                {product.images.map((img, idx) => (
                  <button
                    key={`${img}-${idx}`}
                    onClick={() => { setSelectedImageIndex(idx); setUseColorImage(false); setUseSizeImage(false); }}
                    className={`w-16 h-16 rounded-lg overflow-hidden border-2 shrink-0 transition-all ${
                      selectedImageIndex === idx ? 'border-[#2a9d8f] ring-1 ring-[#2a9d8f]/30' : 'border-slate-200 hover:border-slate-300'
                    }`}
                    data-testid={`thumbnail-${idx}`}
                  >
                    <img
                      src={resolveImageUrl(img)}
                      alt={`${product.name} ${idx + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Right - Details */}
          <div className="space-y-8">
            <div>
              <Link 
                to="/produkter"
                className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-primary mb-4"
              >
                <ChevronLeft className="w-4 h-4" />
                Tillbaka
              </Link>
              <h1 className="text-3xl md:text-4xl font-bold text-slate-900" data-testid="product-name">
                {product.name}
              </h1>
              {(() => {
                let displayPrice = product.price;
                let displayOldPrice = product.old_price;
                if (hasAdminSizePricing && selectedPrintSize) {
                  const quality = selectedPrintQuality || 'Standard';
                  const entry = product.size_quality_prices?.find(p => p.size === selectedPrintSize && (p.quality || 'Standard') === quality);
                  if (entry) {
                    displayPrice = entry.price;
                    if (entry.old_price != null && entry.old_price !== '') {
                      displayOldPrice = entry.old_price;
                    }
                  }
                }
                // Respect sale window
                const now = Date.now();
                const startsMs = product.sale_starts_at ? Date.parse(product.sale_starts_at) : null;
                const endsMs = product.sale_ends_at ? Date.parse(product.sale_ends_at) : null;
                const saleActive = (!startsMs || now >= startsMs) && (!endsMs || now <= endsMs);
                const showOldPrice = saleActive && displayOldPrice != null && displayOldPrice !== '' && Number(displayOldPrice) > Number(displayPrice);
                const discountPct = showOldPrice
                  ? Math.round(((Number(displayOldPrice) - Number(displayPrice)) / Number(displayOldPrice)) * 100)
                  : null;
                // Countdown / date label
                let saleWindowLabel = null;
                if (showOldPrice && endsMs) {
                  const diffMs = endsMs - now;
                  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
                  const hours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
                  if (diffMs > 0) {
                    if (days >= 1) saleWindowLabel = `Rean slutar om ${days} dag${days !== 1 ? 'ar' : ''}`;
                    else if (hours >= 1) saleWindowLabel = `Rean slutar om ${hours} timmar`;
                    else saleWindowLabel = 'Rean slutar snart!';
                  }
                }
                if (showOldPrice && !endsMs && startsMs && now < startsMs) saleWindowLabel = null;
                return (
                  <>
                    <div className="mt-4 flex items-baseline gap-3 flex-wrap">
                      <p className="text-2xl font-semibold text-primary" data-testid="product-price">
                        {displayPrice} kr
                      </p>
                      {showOldPrice && (
                        <p className="text-lg font-medium text-slate-400 line-through" data-testid="product-old-price">
                          {Number(displayOldPrice).toLocaleString('sv-SE')} kr
                        </p>
                      )}
                      {discountPct > 0 && (
                        <span className="bg-[#e76f51] text-white text-xs font-bold px-2 py-0.5 rounded" data-testid="product-discount-badge">
                          -{discountPct}%
                        </span>
                      )}
                      <span className="text-sm font-normal text-slate-500">inkl. 25% moms</span>
                    </div>
                    {saleWindowLabel && (
                      <p className="mt-2 text-sm font-medium text-[#e76f51] flex items-center gap-1.5" data-testid="sale-countdown">
                        <span className="inline-block w-2 h-2 rounded-full bg-[#e76f51] animate-pulse"></span>
                        {saleWindowLabel}
                      </p>
                    )}
                  </>
                );
              })()}
            </div>

            <div
              className={RICH_TEXT_CLASSES}
              data-testid="product-description"
              dangerouslySetInnerHTML={{ __html: sanitizeHTML(product.description) }}
            />

            {/* Color Selection */}
            {product.colors?.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-3">
                  Färg: <span className="text-slate-500">{selectedColor}</span>
                </label>
                <div className="flex flex-wrap gap-3">
                  {product.colors.map((color) => (
                    <button
                      key={color}
                      onClick={() => { setSelectedColor(color); setUseColorImage(true); }}                      className={`w-10 h-10 rounded-full border-2 transition-all flex items-center justify-center ${
                        selectedColor === color 
                          ? 'border-primary scale-110' 
                          : 'border-slate-200 hover:border-slate-400'
                      }`}
                      style={{ backgroundColor: colorHexMap[color] || '#ddd' }}
                      data-testid={`color-${color}`}
                      title={color}
                    >
                      {selectedColor === color && (
                        <Check className={`w-4 h-4 ${['Svart', 'Marinblå', 'Mörkgrå', 'Brun', 'Bordeaux', 'Olivgrön', 'Lila'].includes(color) ? 'text-white' : 'text-slate-700'}`} />
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Size Selection (legacy dropdown — hidden when new size pricing is set) */}
            {product.sizes?.length > 0 && !hasAdminSizePricing && (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-3">
                  Storlek
                </label>
                <Select value={selectedSize} onValueChange={(v) => { setSelectedSize(v); setUseSizeImage(true); }}>
                  <SelectTrigger className="w-full" data-testid="size-select">
                    <SelectValue placeholder="Välj storlek" />
                  </SelectTrigger>
                  <SelectContent>
                    {product.sizes.map((size) => (
                      <SelectItem key={size} value={size}>{size}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Photo Print Size & Quality selector */}
            {hasAdminSizePricing && (
              <div className="space-y-4 p-4 bg-slate-50 rounded-xl border" data-testid="photo-print-options">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Storlek</label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {product.available_sizes.map(size => (
                      <button key={size} onClick={() => { setSelectedPrintSize(size); setUseSizeImage(true); }}
                        className={`py-2 px-3 rounded-lg text-sm font-medium border-2 transition-all ${
                          selectedPrintSize === size ? 'border-[#2a9d8f] bg-[#2a9d8f]/10 text-[#2a9d8f]' : 'border-slate-200 text-slate-600 hover:border-slate-300'
                        }`} data-testid={`print-size-${size}`}>
                        {size}
                      </button>
                    ))}
                  </div>
                </div>
                {hasQualities && (
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Kvalitet</label>
                    <div className="flex flex-wrap gap-2">
                      {product.available_qualities.map(q => (
                        <button key={q} onClick={() => setSelectedPrintQuality(q)}
                          className={`py-2 px-4 rounded-lg text-sm font-medium border-2 transition-all ${
                            selectedPrintQuality === q ? 'border-[#2a9d8f] bg-[#2a9d8f]/10 text-[#2a9d8f]' : 'border-slate-200 text-slate-600 hover:border-slate-300'
                          }`} data-testid={`print-quality-${q}`}>
                          {q}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Quantity */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-3">
                Antal
              </label>
              <div className="flex items-center gap-4">
                <button
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="w-10 h-10 rounded-full border border-slate-200 hover:border-primary transition-colors flex items-center justify-center"
                  data-testid="quantity-decrease"
                >
                  -
                </button>
                <span className="text-lg font-medium w-8 text-center" data-testid="quantity-value">
                  {quantity}
                </span>
                <button
                  onClick={() => setQuantity(quantity + 1)}
                  className="w-10 h-10 rounded-full border border-slate-200 hover:border-primary transition-colors flex items-center justify-center"
                  data-testid="quantity-increase"
                >
                  +
                </button>
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <Button 
                onClick={handleDesign}
                className="btn-primary flex-1"
                data-testid="design-button"
              >
                <Palette className="w-5 h-5 mr-2" />
                {(() => {
                  if (product?.model_type === 'calendar' || product?.category === 'kalender') return 'Skapa din kalender';
                  if (product?.model_type === 'nametag' || product?.category === 'namnskylt') return 'Skapa namnlappar';
                  if (product?.category === 'fotoalbum') return 'Skapa ditt fotoalbum';
                  return 'Designa med egen bild';
                })()}
              </Button>
              <Button 
                onClick={handleAddToCart}
                variant="outline"
                className="btn-outline flex-1"
                disabled={adding}
                data-testid="add-to-cart-button"
              >
                {adding ? (
                  <div className="spinner w-5 h-5 mr-2" />
                ) : (
                  <ShoppingCart className="w-5 h-5 mr-2" />
                )}
                Lägg i varukorg
              </Button>
            </div>

            {/* Expanderbar detaljerad beskrivning */}
            {product.long_description && (
              <details className="border border-slate-200 rounded-xl overflow-hidden group" data-testid="product-long-description">
                <summary className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-slate-50 transition-colors list-none">
                  <span className="text-xl font-bold text-slate-900">Om Produkten</span>
                  <ChevronDown className="w-6 h-6 text-slate-400 transition-transform group-open:rotate-180" />
                </summary>
                <div
                  className={`px-5 pb-5 pt-1 text-sm ${RICH_TEXT_CLASSES} border-t border-slate-100`}
                  dangerouslySetInnerHTML={{ __html: sanitizeHTML(product.long_description) }}
                />
              </details>
            )}

            {/* Features */}
            <div className="border-t pt-8 space-y-4">
              <div className="flex items-start gap-3">
                <Check className="w-5 h-5 text-accent mt-0.5" />
                <div>
                  <p className="font-medium text-slate-800">Hög kvalité</p>
                  <p className="text-sm text-slate-500">Professionellt tryck som håller</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Check className="w-5 h-5 text-accent mt-0.5" />
                <div>
                  <p className="font-medium text-slate-800">Snabb leverans</p>
                  <p className="text-sm text-slate-500">2-5 arbetsdagar</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Check className="w-5 h-5 text-accent mt-0.5" />
                <div>
                  <p className="font-medium text-slate-800">Nöjd kund-garanti</p>
                  <p className="text-sm text-slate-500">14 dagars öppet köp</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;
